// src/components/brand/edgeLexMarkAssets.ts
//
// The generated brand-mark assets and the rule that picks between them. Kept
// out of EdgeLexMark.tsx so that file exports only its component
// (react-refresh).
//
// There are two families, and the RENDERED SIZE decides which is correct:
//
//   open/      the mark on a transparent ground, one variant per tone.
//              Correct at 48px and up, where the artwork resolves.
//   contained/ the mark on a light disc. One asset serves BOTH modes:
//              on a dark surface the disc supplies the hard silhouette the
//              artwork cannot supply itself, and on a light surface the disc
//              falls into the background so what reads is the open mark.
//              Correct at 32px and below.
//
// The contained form exists because the artwork carries about twenty hairline
// traces plus a modelled face, and below roughly 32px none of that resolves at
// any tone. Lifting the ink produces a lighter blob, not a clearer mark. This
// was shipped and reverted once — do not "simplify" it back to one family.
//
// `open/` is keyed by the SURFACE the mark sits on, not by the colour of the
// art: open/dark/ holds the light-ink art that survives a dark background.
//
// All of it comes from one master via `scripts/brand/generate-brand-marks.py`
// at the repo root; never hand-edit the PNGs.

import containedMark32 from '@/assets/brand/contained/edgelex-mark-32.png';
import containedMark64 from '@/assets/brand/contained/edgelex-mark-64.png';
import containedMark128 from '@/assets/brand/contained/edgelex-mark-128.png';
import darkMark128 from '@/assets/brand/open/dark/edgelex-mark-128.png';
import darkMark256 from '@/assets/brand/open/dark/edgelex-mark-256.png';
import darkMark512 from '@/assets/brand/open/dark/edgelex-mark-512.png';
import lightMark128 from '@/assets/brand/open/light/edgelex-mark-128.png';
import lightMark256 from '@/assets/brand/open/light/edgelex-mark-256.png';
import lightMark512 from '@/assets/brand/open/light/edgelex-mark-512.png';

/** Which surface the mark is being placed on. `auto` follows the app theme. */
export type EdgeLexMarkSurface = 'auto' | 'light' | 'dark';

/** A resolved surface — what the open-form lookup is keyed on. */
export type EdgeLexMarkTone = Exclude<EdgeLexMarkSurface, 'auto'>;

/**
 * At or below this rendered CSS size the contained form is used, in both
 * modes. The generator holds the same number as CONTAINED_MAX_RENDER_SIZE.
 */
export const CONTAINED_MAX_RENDER_SIZE = 32;

const CONTAINED_BUCKETS = [32, 64, 128] as const;
const OPEN_BUCKETS = [128, 256, 512] as const;

const CONTAINED_SOURCES: Record<(typeof CONTAINED_BUCKETS)[number], string> = {
  32: containedMark32,
  64: containedMark64,
  128: containedMark128,
};

const OPEN_SOURCES: Record<EdgeLexMarkTone, Record<(typeof OPEN_BUCKETS)[number], string>> = {
  light: { 128: lightMark128, 256: lightMark256, 512: lightMark512 },
  dark: { 128: darkMark128, 256: darkMark256, 512: darkMark512 },
};

const pickBucket = <T extends number>(buckets: readonly T[], size: number): T =>
  buckets.find((bucket) => bucket >= size * 2) ?? buckets[buckets.length - 1];

/** True when the rendered size is too small for the artwork to resolve. */
export function isContainedSize(size: number): boolean {
  return size <= CONTAINED_MAX_RENDER_SIZE;
}

/**
 * The asset for a rendered size on a resolved surface. Small sizes get the
 * contained form regardless of tone; larger sizes get the open form for that
 * tone. Buckets cover a 2x display, so a 28px rail slot loads the 64px asset.
 */
export function markSourceFor(size: number, tone: EdgeLexMarkTone): string {
  return isContainedSize(size)
    ? CONTAINED_SOURCES[pickBucket(CONTAINED_BUCKETS, size)]
    : OPEN_SOURCES[tone][pickBucket(OPEN_BUCKETS, size)];
}
